import type { Example, ExercisePayload, Item, MatchingPair, Skill } from "./types";

export const studySkills: Array<[Skill, string]> = [
  ["recognition", "Flashcard"],
  ["dictation", "Nghe - viết"],
  ["production", "Chọn từ theo ngữ cảnh"],
  ["meaning_choice", "Chọn nghĩa đúng"],
  ["word_choice", "Chọn từ đúng"],
  ["matching", "Nối từ với nghĩa"],
  ["audio_word_choice", "Nghe và chọn từ"],
  ["audio_meaning_choice", "Nghe và chọn nghĩa"],
  ["spelling_correction", "Cụm từ trong câu"],
  ["missing_letters", "Hiểu nghĩa trong câu"],
];

export const skillLabels = Object.fromEntries(studySkills) as Record<Skill, string>;
export const studySkillValues = studySkills.map(([skill]) => skill);

type ExerciseItem = Pick<Item, "id" | "term" | "meaning_vi" | "definition_en" | "part_of_speech" | "examples" | "forms">;

function hash(value: string) {
  return [...value].reduce((total, character) => (total * 31 + character.charCodeAt(0)) >>> 0, 7);
}

function shuffle<T>(values: T[], seed: number) {
  const output = [...values];
  let state = seed || 1;
  for (let index = output.length - 1; index > 0; index -= 1) {
    state = (state * 1664525 + 1013904223) >>> 0;
    const swapIndex = state % (index + 1);
    [output[index], output[swapIndex]] = [output[swapIndex], output[index]];
  }
  return output;
}

function distractors(items: ExerciseItem[], target: ExerciseItem, field: "term" | "meaning_vi") {
  const samePart = items.filter((item) => item.id !== target.id && item.part_of_speech === target.part_of_speech && item[field]);
  const all = items.filter((item) => item.id !== target.id && item[field]);
  return shuffle([...samePart, ...all.filter((item) => !samePart.some((candidate) => candidate.id === item.id))], hash(`${target.id}:${field}`)).slice(0, 3);
}

function uniqueValues(values: string[]) {
  return [...new Set(values.filter(Boolean))];
}

function curatedDistractors(target: ExerciseItem, field: "term" | "meaning_vi") {
  const values = Array.isArray(target.forms?.distractors) ? target.forms.distractors : [];
  return values.map((value) => value && typeof value === "object" ? value as Record<string, unknown> : null)
    .filter(Boolean)
    .map((value) => String(value?.[field] ?? "").trim())
    .filter(Boolean)
    .slice(0, 3);
}

function optionExplanations(target: ExerciseItem) {
  const output: Record<string, string> = {};
  const values = Array.isArray(target.forms?.distractors) ? target.forms.distractors : [];
  for (const value of values) {
    if (!value || typeof value !== "object") continue;
    const record = value as Record<string, unknown>;
    const explanation = String(record.explanation ?? "").trim();
    if (!explanation) continue;
    const term = String(record.term ?? "").trim();
    const meaning = String(record.meaning_vi ?? "").trim();
    if (term) output[term] = explanation;
    if (meaning) output[meaning] = explanation;
  }
  return output;
}

function matchingPairs(items: ExerciseItem[], target: ExerciseItem): MatchingPair[] {
  const related = items.filter((item) => item.meaning_vi && item.term);
  const pool = shuffle(related.filter((item) => item.id !== target.id), hash(`${target.id}:matching`)).slice(0, 7);
  const pairs = [target, ...pool].filter((item, index, values) => values.findIndex((candidate) => candidate.term === item.term || candidate.meaning_vi === item.meaning_vi) === index).slice(0, 8);
  return shuffle(pairs, hash(`${target.id}:matching-order`)).map((item) => ({ term: item.term, meaning: item.meaning_vi }));
}

export function selectExample(target: ExerciseItem, skill: Skill): Example | null {
  const examples = (target.examples ?? []).filter((example) => example.sentence_en && example.translation_vi);
  if (!examples.length) return null;
  return examples[hash(`${target.id}:${skill}:example`) % examples.length];
}

function cloze(sentence: string, term: string) {
  const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return sentence.replace(new RegExp(`\\b${escaped}\\b`, "ig"), "_____");
}

function toeicMiniPassage(example: Example) {
  return {
    sentence: `Notice: ${example.sentence_en} Please contact the office if you need more information.`,
    translation: `${example.translation_vi} Vui lòng liên hệ văn phòng nếu bạn cần thêm thông tin.`,
  };
}

function contextChoice(skill: Skill, target: ExerciseItem, items: ExerciseItem[], preferredExample?: Example | null): ExercisePayload | null {
  const example = preferredExample ?? selectExample(target, skill);
  if (!example) return null;
  if (skill === "missing_letters") {
    const passage = toeicMiniPassage(example);
    return {
      prompt: "Đọc thông báo ngắn và chọn nghĩa phù hợp với từ được dùng trong đoạn.",
      context_sentence: passage.sentence,
      context_translation: passage.translation,
      options: shuffle(uniqueValues([target.meaning_vi, ...curatedDistractors(target, "meaning_vi"), ...distractors(items, target, "meaning_vi").map((item) => item.meaning_vi)]).slice(0, 4), hash(`${target.id}:${skill}:context-meaning`)),
      answer: target.meaning_vi,
      focus: "meaning",
      explanation: `${target.term} trong câu này mang nghĩa "${target.meaning_vi}".`,
      option_explanations: optionExplanations(target),
    };
  }
  const sentence = cloze(example.sentence_en, target.term);
  if (sentence === example.sentence_en) return null;
  return {
    prompt: skill === "production" ? "Chọn từ phù hợp nhất để hoàn thành câu." : "Chọn từ tạo thành cách dùng tự nhiên trong câu.",
    context_sentence: sentence,
    context_translation: example.translation_vi,
    options: shuffle(uniqueValues([target.term, ...curatedDistractors(target, "term"), ...distractors(items, target, "term").map((item) => item.term)]).slice(0, 4), hash(`${target.id}:${skill}:context-term`)),
    answer: target.term,
    focus: "term",
    explanation: `${target.term} nghĩa là "${target.meaning_vi}" và phù hợp với ngữ cảnh của câu.`,
    option_explanations: optionExplanations(target),
  };
}

export function buildExercise(skill: Skill, target: ExerciseItem, items: ExerciseItem[], preferredExample?: Example | null): ExercisePayload | null {
  switch (skill) {
    case "production":
    case "spelling_correction":
    case "missing_letters":
      return contextChoice(skill, target, items, preferredExample);
    case "meaning_choice":
      return { prompt: "Chọn nghĩa tiếng Việt phù hợp với từ này.", options: shuffle(uniqueValues([target, ...distractors(items, target, "meaning_vi")].map((item) => item.meaning_vi)), hash(`${target.id}:meaning-choice`)) };
    case "word_choice":
      return { prompt: "Chọn từ tiếng Anh phù hợp với nghĩa này.", options: shuffle(uniqueValues([target, ...distractors(items, target, "term")].map((item) => item.term)), hash(`${target.id}:word-choice`)) };
    case "matching":
      return { prompt: "Ghép mỗi từ tiếng Anh với nghĩa tiếng Việt tương ứng.", matching_pairs: matchingPairs(items, target) };
    case "audio_word_choice":
      return { prompt: "Nghe và chọn từ bạn nghe được.", options: shuffle(uniqueValues([target, ...distractors(items, target, "term")].map((item) => item.term)), hash(`${target.id}:audio-word-choice`)) };
    case "audio_meaning_choice":
      return { prompt: "Nghe và chọn nghĩa phù hợp.", options: shuffle(uniqueValues([target, ...distractors(items, target, "meaning_vi")].map((item) => item.meaning_vi)), hash(`${target.id}:audio-meaning-choice`)) };
    default:
      return null;
  }
}
